import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import type { ThemeName, ThemeColors, ThemeContextValue } from "@/types";

export const THEMES: Record<ThemeName, ThemeColors> = {
  dark: {
    bg: "#050508", s1: "#0c0c14", s2: "#131320", card: "#161624", cardH: "#1c1c2e",
    bdr: "#222236", bdrL: "#2d2d48", pri: "#7C3AED", priH: "#A78BFA",
    priG: "rgba(124,58,237,0.15)", ok: "#10b981", okBg: "rgba(16,185,129,0.1)",
    warn: "#f59e0b", warnBg: "rgba(245,158,11,0.1)", err: "#ef4444",
    txt: "#f0f0f8", tx2: "#8b8fa8", tx3: "#5c5f76",
    tent: "#f59e0b", tentBg: "rgba(245,158,11,0.1)",
  },
  light: {
    bg: "#f5f5f7", s1: "#ffffff", s2: "#f0f0f3", card: "#ffffff", cardH: "#f8f8fb",
    bdr: "#e0e0e8", bdrL: "#d0d0da", pri: "#7C3AED", priH: "#6d28d9",
    priG: "rgba(124,58,237,0.1)", ok: "#059669", okBg: "rgba(5,150,105,0.08)",
    warn: "#d97706", warnBg: "rgba(217,119,6,0.08)", err: "#dc2626",
    txt: "#1a1a2e", tx2: "#555570", tx3: "#8888a0",
    tent: "#d97706", tentBg: "rgba(217,119,6,0.08)",
  },
};

interface ExtendedThemeContextValue extends ThemeContextValue {
  setAccentColor: (color: string) => void;
}

const extendedDefaultContext: ExtendedThemeContextValue = {
  theme: "dark",
  colors: THEMES.dark,
  toggle: () => {},
  setAccentColor: () => {},
};

export const ThemeContext = createContext<ExtendedThemeContextValue>(extendedDefaultContext);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemeName>("dark");
  const [, setCustomAccentColor] = useState<string | null>(null);
  const colors = THEMES[theme];

  useEffect(() => {
    const root = document.documentElement;
    // Set CSS custom properties for Tailwind integration
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--qs-${key}`, value);
    });
    document.body.style.background = colors.bg;
    document.body.style.color = colors.txt;
    document.body.className = theme === "light" ? "light-mode" : "";
  }, [theme, colors]);

  const setAccentColor = (color: string) => {
    setCustomAccentColor(color);
    const root = document.documentElement;
    // Parse hex color and create lighter/darker variants
    // Using a simple approach: set primary color tokens
    root.style.setProperty(`--qs-pri`, color);

    // Generate a lighter variant (for hover states)
    const lighterColor = lightenColor(color, 20);
    root.style.setProperty(`--qs-priH`, lighterColor);

    // Generate a gradient variant
    root.style.setProperty(`--qs-priG`, `${color}26`); // ~15% opacity
  };

  const toggle = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <ThemeContext.Provider value={{ theme, colors, toggle, setAccentColor }}>
      {children}
    </ThemeContext.Provider>
  );
}

// Helper to lighten a hex color
function lightenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return (
    "#" +
    (0x1000000 + (R < 255 ? R : 255) * 0x10000 + (G < 255 ? G : 255) * 0x100 + (B < 255 ? B : 255))
      .toString(16)
      .slice(1)
  );
}

export function useTheme(): ExtendedThemeContextValue {
  return useContext(ThemeContext);
}
